/**
 * Loyalty Rive Loader
 * ───────────────────────────────────────────────────────────────
 * Lazily initialises Rive animations on <canvas data-rive-src> elements.
 *
 * - Loads the Rive WASM runtime only when the first canvas enters the viewport.
 * - Lazy-loads each canvas via a "load" observer (rootMargin: 200px).
 * - Plays ONLY the canvas that is centred/dominant in the viewport via a
 *   separate "play" observer — neighbours in a carousel stay paused so they
 *   don't fight the scroll gesture for the main thread.
 * - Caps the canvas backing store at 2x DPR to keep per-frame cost down.
 *
 * Include this script once in the layout or on the rewards page:
 *   <script src="{{ 'loyalty-rive-loader.js' | asset_url }}" defer></script>
 */
(function () {
  'use strict';

  var RIVE_CDN = 'https://unpkg.com/@rive-app/canvas@2.21.6';
  var MAX_DPR = 2;       /* Cap backing-store size on high-DPI phones */
  var PLAY_RATIO = 0.6;  /* A canvas must be ≥60% visible to animate */
  var riveModule = null;
  var riveLoading = null;
  var instances = [];
  var playObserver = null;

  /**
   * Capped device pixel ratio — limits how large the canvas buffer Rive
   * re-rasterises every frame on high-density mobile screens.
   */
  function getDpr() {
    return Math.min(window.devicePixelRatio || 1, MAX_DPR);
  }

  function findInstance(canvas) {
    return instances.find(function (i) { return i.canvas === canvas; });
  }

  /**
   * Load the Rive runtime from CDN (once).
   */
  function loadRiveRuntime() {
    if (riveModule) return Promise.resolve(riveModule);
    if (riveLoading) return riveLoading;

    riveLoading = new Promise(function (resolve, reject) {
      var script = document.createElement('script');
      script.src = RIVE_CDN;
      script.onload = function () {
        if (window.rive) {
          riveModule = window.rive;
          resolve(riveModule);
        } else {
          reject(new Error('Rive runtime not found on window.rive'));
        }
      };
      script.onerror = function () {
        reject(new Error('Failed to load Rive runtime from CDN'));
      };
      document.head.appendChild(script);
    });

    return riveLoading;
  }

  /**
   * Initialise a single Rive canvas.
   */
  function initCanvas(canvas) {
    var src = canvas.dataset.riveSrc;
    if (!src || canvas.dataset.riveReady) return;
    canvas.dataset.riveReady = 'loading';

    loadRiveRuntime().then(function (rive) {
      /* Size canvas to container (DPR capped) */
      var rect = canvas.getBoundingClientRect();
      var dpr = getDpr();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;

      var config = {
        src: src,
        canvas: canvas,
        autoplay: true,
        /* Don't attach Rive's own pointer/touch hit-testing — it runs on
           every touchmove and makes carousel swiping janky. Re-enable
           per-canvas only if a .riv needs pointer interaction. */
        automaticallyHandleEvents: false,
      };

      /* Optional state machine name */
      var stateName = canvas.dataset.riveState;
      if (stateName) {
        config.stateMachines = stateName;
      }

      var riveInstance = new rive.Rive(config);

      canvas.dataset.riveReady = 'true';

      instances.push({
        canvas: canvas,
        instance: riveInstance,
      });

      /* Hand play/pause control to the visibility observer so only the
         centred card animates. */
      if (playObserver) playObserver.observe(canvas);
    }).catch(function (err) {
      console.warn('[loyalty-rive] Failed to init:', src, err);
      canvas.dataset.riveReady = 'error';
    });
  }

  /**
   * Play or pause a canvas's Rive instance (pausing stops its rAF render
   * loop, freeing the main thread for scrolling).
   */
  function setPlayState(canvas, shouldPlay) {
    var match = findInstance(canvas);
    if (!match) return;
    if (shouldPlay) {
      if (match.instance.play) match.instance.play();
    } else {
      if (match.instance.pause) match.instance.pause();
    }
  }

  /**
   * Observer that plays only the canvas dominant in the *real* viewport.
   * No rootMargin here (unlike the load observer) so intersectionRatio is
   * measured against the actual screen — a carousel neighbour peeking in
   * stays below PLAY_RATIO and remains paused.
   */
  function createPlayObserver() {
    if (!('IntersectionObserver' in window)) return null;
    return new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        setPlayState(entry.target, entry.intersectionRatio >= PLAY_RATIO);
      });
    }, { threshold: [0, PLAY_RATIO, 1] });
  }

  /**
   * Lazy-load canvases as they approach the viewport.
   */
  function observeCanvases() {
    var canvases = document.querySelectorAll('canvas[data-rive-src]:not([data-rive-ready])');
    if (canvases.length === 0) return;

    if (!('IntersectionObserver' in window)) {
      /* Fallback: init all immediately (no play gating available) */
      canvases.forEach(initCanvas);
      return;
    }

    var loadObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting && !entry.target.dataset.riveReady) {
          initCanvas(entry.target);
          loadObserver.unobserve(entry.target);
        }
      });
    }, { rootMargin: '200px' });

    canvases.forEach(function (c) { loadObserver.observe(c); });
  }

  /**
   * Handle window resize — update canvas dimensions.
   *
   * Width-only guard: on mobile, scrolling shows/hides the URL bar which
   * fires `resize` with a changed *height*. Reallocating every canvas
   * backing store mid-scroll causes a visible hitch, so ignore those.
   */
  var resizeTimer;
  var lastWidth = window.innerWidth;
  window.addEventListener('resize', function () {
    if (window.innerWidth === lastWidth) return;
    lastWidth = window.innerWidth;

    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      instances.forEach(function (item) {
        var rect = item.canvas.getBoundingClientRect();
        var dpr = getDpr();
        item.canvas.width = rect.width * dpr;
        item.canvas.height = rect.height * dpr;
        if (item.instance.resizeDrawingSurfaceToCanvas) {
          item.instance.resizeDrawingSurfaceToCanvas();
        }
      });
    }, 150);
  });

  /* Create the play observer before any canvas inits so initCanvas can
     hand off control immediately. */
  playObserver = createPlayObserver();

  /* Init on DOM ready */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observeCanvases);
  } else {
    observeCanvases();
  }

  /* Re-scan after Yotpo widgets render (they may inject new canvases) */
  document.addEventListener('yotpo-loyalty:ready', function () {
    setTimeout(observeCanvases, 500);
  });

  /* Also watch for dynamically added canvases */
  if ('MutationObserver' in window) {
    var domObserver = new MutationObserver(function (mutations) {
      var hasNewCanvas = mutations.some(function (m) {
        return Array.from(m.addedNodes).some(function (n) {
          return n.nodeType === 1 && (
            (n.tagName === 'CANVAS' && n.dataset.riveSrc) ||
            n.querySelector && n.querySelector('canvas[data-rive-src]')
          );
        });
      });
      if (hasNewCanvas) observeCanvases();
    });
    domObserver.observe(document.body, { childList: true, subtree: true });
  }
})();
